import apiLambdaInsumo from "./apiLambdaInsumo"

// Importação de Notas de Compra (lambda ficha-tecnica, rotas /notaCompra).

export interface ErroApi {
    retornoErro: number
    mensagem: string
}

export interface ItemImportacao {
    codigoProduto: string
    ean: string | null
    descricao: string
    unidade: string
    quantidade: number
    valorUnitario: number
    insumoId: number | null
    fatorConversao: number
}

export interface ImportarNotaPayload {
    chave: string
    numero: string
    serie: string
    dataEmissao: string | null
    fornecedorCnpj: string
    fornecedorNome: string
    valorTotal: number
    itens: ItemImportacao[]
}

export interface SugestaoVinculo {
    codigoProduto: string
    insumoId: number | null
    fatorConversao: number
    origem: "VINCULO" | "EAN" | null
}

export interface LancamentoImportacao {
    insumoId: number
    insumo: string
    quantidadeEntrada: number
    saldoAnterior: number
    saldoAtual: number
    custoUnitario: number
}

export interface ResultadoImportacao {
    mensagem: string
    notaCompraId: number
    itensLancados: number
    itensIgnorados: number
    lancamentos: LancamentoImportacao[]
}

export interface NotaCompraImportada {
    id: number
    chave: string
    numero: string | null
    serie: string | null
    data_emissao: string | null
    fornecedor_cnpj: string | null
    fornecedor_nome: string | null
    valor_total: string | number | null
    quantidade_itens: number | null
    itens_lancados: number | null
    usuario: string | null
    created_at: string
}

function headers(autorizacao: string) {
    return { "Content-Type": "application/json", Authorization: autorizacao }
}

function erro(error: unknown): ErroApi {
    const e = error as { message?: string; response?: { data?: unknown } }
    const data = e.response?.data as { mensagem?: string } | string | undefined
    if (data && typeof data === "object" && data.mensagem) {
        return { retornoErro: 1, mensagem: data.mensagem }
    }
    if (typeof data === "string" && data) return { retornoErro: 1, mensagem: data }
    if (e.message === "Network Error") return { retornoErro: 1, mensagem: "Verifique sua conexão!" }
    return { retornoErro: 1, mensagem: e.message || "Erro inesperado na requisição!" }
}

export async function verificarNotaCompra(
    idEstabelecimento: number,
    chave: string,
    autorizacao: string
): Promise<{ importada: boolean; nota: NotaCompraImportada | null } | ErroApi> {
    try {
        const r = await apiLambdaInsumo.get(`/notaCompra/verificar/${idEstabelecimento}/${chave}`, {
            headers: headers(autorizacao),
        })
        return r.data
    } catch (e) {
        return erro(e)
    }
}

export async function sugerirVinculos(
    idEstabelecimento: number,
    fornecedorCnpj: string,
    itens: { codigoProduto: string; ean: string | null }[],
    autorizacao: string
): Promise<{ sugestoes: SugestaoVinculo[] } | ErroApi> {
    try {
        const r = await apiLambdaInsumo.post(
            `/notaCompra/sugestoes/${idEstabelecimento}`,
            {
                fornecedorCnpj,
                itens: itens.map(i => ({ codigoProduto: i.codigoProduto, ean: i.ean ?? "" })),
            },
            { headers: headers(autorizacao) }
        )
        return r.data
    } catch (e) {
        return erro(e)
    }
}

export async function importarNotaCompra(
    idEstabelecimento: number,
    payload: ImportarNotaPayload,
    autorizacao: string
): Promise<ResultadoImportacao | ErroApi> {
    try {
        const r = await apiLambdaInsumo.post(`/notaCompra/importar/${idEstabelecimento}`, payload, {
            headers: headers(autorizacao),
        })
        return r.data
    } catch (e) {
        return erro(e)
    }
}

export interface EstornoCancelamento {
    insumoId: number
    insumo: string
    quantidadeSaida: number
    saldoAnterior: number
    saldoAtual: number
}

export interface ResultadoCancelamento {
    mensagem: string
    estoqueRevertido: boolean
    estornos: EstornoCancelamento[]
    insumosNegativos: EstornoCancelamento[]
}

export async function cancelarNotaCompra(
    idEstabelecimento: number,
    notaCompraId: number,
    reverterEstoque: boolean,
    autorizacao: string
): Promise<ResultadoCancelamento | ErroApi> {
    try {
        const r = await apiLambdaInsumo.post(
            `/notaCompra/cancelar/${idEstabelecimento}/${notaCompraId}`,
            { reverterEstoque },
            { headers: headers(autorizacao) }
        )
        return r.data
    } catch (e) {
        return erro(e)
    }
}

export async function listarNotasCompra(
    idEstabelecimento: number,
    autorizacao: string
): Promise<NotaCompraImportada[] | ErroApi> {
    try {
        const r = await apiLambdaInsumo.get(`/notaCompra/listar/${idEstabelecimento}`, {
            headers: headers(autorizacao),
        })
        return Array.isArray(r.data) ? r.data : []
    } catch (e) {
        return erro(e)
    }
}
